import { create } from 'zustand';
import { api } from '../lib/api';

export type LeadChip = 'callable' | 'followUpsDue' | 'untouched' | 'slaRisk' | 'mySqls';

export interface LeadFilters {
  q: string;
  rep: string;
  campaign: string;
  specialty: string;
  status: string;
  source: string;
  state: string;
  tz: string;
  aging: string;
}

/** The My Leads list UI state. Persisting it here (instead of component useState) is what keeps a
 * rep's filters/sort/page intact when they open a lead and come back — previously it was wiped. */
export interface LeadListState {
  filters: LeadFilters;
  chips: Record<LeadChip, boolean>;
  sort: string;
  dir: 'asc' | 'desc';
  page: number;
}

/** Lightweight row info kept for the Scripts working-queue sidebar / auto-advance. */
export interface QueueItem {
  id: string;
  company: string;
  specialty: string;
  timezone: string;
}

interface WorkQueue {
  items: QueueItem[];
  /** Base list params (filters + active chips + sort/dir); no page/pageSize/includeTotal. */
  params: Record<string, unknown>;
  pageSize: number;
  /** List page number that items[0] came from (the page the rep was viewing). */
  startPage: number;
  /** Highest list page currently merged into items. */
  loadedThroughPage: number;
  total: number;
  /** Lead ids already dispositioned this pass — advance skips them and the readout counts them. */
  worked: string[];
}

const DEFAULT_LIST: LeadListState = {
  filters: { q: '', rep: '', campaign: '', specialty: '', status: '', source: '', state: '', tz: '', aging: '' },
  chips: { callable: false, followUpsDue: false, untouched: false, slaRisk: false, mySqls: false },
  sort: 'leadDate',
  dir: 'desc',
  page: 1,
};

const toQueueItem = (r: {
  id: string;
  company?: string;
  specialty?: string;
  timezone?: string;
}): QueueItem => ({
  id: r.id,
  company: r.company ?? '',
  specialty: r.specialty ?? '',
  timezone: r.timezone ?? '',
});

interface LeadQueueStore {
  list: LeadListState;
  setFilters: (patch: Partial<LeadFilters>, resetPage?: boolean) => void;
  toggleChip: (key: LeadChip) => void;
  toggleSort: (col: string) => void;
  /** Set sort column + direction directly (used by the "Smart order" next-best-lead toggle). */
  setSort: (col: string, dir?: 'asc' | 'desc') => void;
  setPage: (page: number) => void;

  /** The ordered list the rep is calling through, published by the My Leads page. */
  queue: WorkQueue | null;
  publishQueue: (
    rows: Array<{ id: string; company?: string; specialty?: string; timezone?: string }>,
    params: Record<string, unknown>,
    page: number,
    pageSize: number,
    total: number,
  ) => void;
  /** Resolve the next lead to work after `id`, fetching the next page on demand. Null at the end. */
  advanceFrom: (id: string) => Promise<string | null>;
  /** Mark a lead worked (dispositioned) so advance skips it and the "N worked" readout updates. */
  markWorked: (id: string) => void;
  /** Reverse markWorked (used by "undo last disposition"). */
  unmarkWorked: (id: string) => void;
}

export const useLeadQueue = create<LeadQueueStore>((set, get) => ({
  list: DEFAULT_LIST,
  setFilters: (patch, resetPage = true) =>
    set((s) => ({
      list: {
        ...s.list,
        filters: { ...s.list.filters, ...patch },
        page: resetPage ? 1 : s.list.page,
      },
    })),
  toggleChip: (key) =>
    set((s) => ({
      list: { ...s.list, chips: { ...s.list.chips, [key]: !s.list.chips[key] }, page: 1 },
    })),
  toggleSort: (col) =>
    set((s) =>
      s.list.sort === col
        ? { list: { ...s.list, dir: s.list.dir === 'asc' ? 'desc' : 'asc' } }
        : { list: { ...s.list, sort: col, dir: 'asc' } },
    ),
  setSort: (col, dir = 'asc') => set((s) => ({ list: { ...s.list, sort: col, dir, page: 1 } })),
  setPage: (page) => set((s) => ({ list: { ...s.list, page } })),

  queue: null,
  publishQueue: (rows, params, page, pageSize, total) =>
    set({
      queue: {
        items: rows.map(toQueueItem),
        params,
        pageSize,
        startPage: page,
        loadedThroughPage: page,
        total,
        worked: [],
      },
    }),
  advanceFrom: async (id) => {
    const initial = get().queue;
    if (!initial) return null;
    let q: WorkQueue = initial;
    const worked = new Set(q.worked);
    let idx = q.items.findIndex((i) => i.id === id);
    if (idx === -1) return null;
    idx += 1;
    // Walk forward to the next not-yet-worked lead, pulling pages on demand. Skipping worked leads
    // keeps auto-advance from ever handing back a lead the rep just dispositioned off the queue.
    for (;;) {
      if (idx >= q.items.length) {
        if (q.loadedThroughPage * q.pageSize >= q.total) return null;
        const nextPage = q.loadedThroughPage + 1;
        try {
          const res = await api.leads({
            ...q.params,
            page: nextPage,
            pageSize: q.pageSize,
            includeTotal: false,
          });
          const known = new Set(q.items.map((i) => i.id));
          const merged: QueueItem[] = [
            ...q.items,
            ...res.rows.map(toQueueItem).filter((i) => !known.has(i.id)),
          ];
          q = { ...q, items: merged, loadedThroughPage: nextPage };
          set({ queue: q });
          if (idx >= q.items.length) return null; // no new rows resolved → end of list
        } catch {
          return null;
        }
      }
      const cand = q.items[idx];
      if (!worked.has(cand.id)) return cand.id;
      idx += 1;
    }
  },
  markWorked: (id) =>
    set((s) =>
      !s.queue || s.queue.worked.includes(id)
        ? {}
        : { queue: { ...s.queue, worked: [...s.queue.worked, id] } },
    ),
  unmarkWorked: (id) =>
    set((s) =>
      !s.queue || !s.queue.worked.includes(id)
        ? {}
        : { queue: { ...s.queue, worked: s.queue.worked.filter((w) => w !== id) } },
    ),
}));
